import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PlusCircle, Save, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HeadCell } from "./userAppShared";
import { UserLoyalty } from "./useUserLoyalty";
import { formatPoints } from "./userLoyalty";

const SummaryTile = ({ color, bg, label, value, icon }: {
  color: string;
  bg: string;
  label: string;
  value: number;
  icon?: JSX.Element;
}) => (
  <Grid size={{ xs: 12, md: 3 }}>
    <Box sx={{ p: 2, borderRadius: 1, backgroundColor: bg }}>
      {icon}
      <Typography variant="h4" sx={{ color, fontWeight: 600, mt: icon ? 1 : 0 }}>
        {formatPoints(value)}
      </Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  </Grid>
);

const UserAppLoyaltyTab = ({ loyalty }: { loyalty: UserLoyalty }) => {
  const { t } = useTranslation();
  const { summary, transactions } = loyalty;
  const na = t("user_app.na");

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <SummaryTile
          color="#be185d"
          bg="rgba(190, 24, 93, 0.08)"
          label={t("user_app.loyalty.available")}
          value={summary.availablePoints}
          icon={<Star size={28} color="#be185d" />}
        />
        <SummaryTile
          color="#2563eb"
          bg="rgba(37, 99, 235, 0.08)"
          label={t("user_app.loyalty.pending")}
          value={summary.pendingPoints}
        />
        <SummaryTile
          color="#16a34a"
          bg="rgba(22, 163, 74, 0.08)"
          label={t("user_app.loyalty.earned")}
          value={summary.lifetimeEarnedPoints}
        />
        <SummaryTile
          color="#ea580c"
          bg="rgba(234, 88, 12, 0.08)"
          label={t("user_app.loyalty.redeemed")}
          value={summary.lifetimeRedeemedPoints}
        />
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PlusCircle size={20} />
              <Typography variant="h6">{t("user_app.loyalty.adjust_title")}</Typography>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={t("user_app.loyalty.points")}
                type="number"
                size="small"
                value={loyalty.adjustmentPoints}
                onChange={(event) => loyalty.setAdjustmentPoints(event.target.value)}
                helperText={t("user_app.loyalty.points_help")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <TextField
                fullWidth
                label={t("user_app.loyalty.reason")}
                size="small"
                value={loyalty.adjustmentReason}
                onChange={(event) => loyalty.setAdjustmentReason(event.target.value)}
                inputProps={{ maxLength: 500 }}
              />
              {loyalty.adjustmentError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {loyalty.adjustmentError}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={loyalty.handleAdjustPoints}
                disabled={loyalty.adjustmentSaving}
                sx={{
                  background: "#ba2156",
                  textTransform: "capitalize",
                  color: "#fff",
                  "&:hover": { background: "#ba2156", color: "#fff" },
                }}
              >
                {loyalty.adjustmentSaving ? t("user_app.detail.saving") : t("btn.save")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loyalty.error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {loyalty.error}
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <HeadCell>{t("user_app.loyalty.col_date")}</HeadCell>
              <HeadCell>{t("user_app.loyalty.col_type")}</HeadCell>
              <HeadCell>{t("user_app.loyalty.col_status")}</HeadCell>
              <HeadCell align="right">{t("user_app.loyalty.col_points")}</HeadCell>
              <HeadCell align="right">{t("user_app.loyalty.col_balance")}</HeadCell>
              <HeadCell>{t("user_app.loyalty.col_source")}</HeadCell>
              <HeadCell>{t("user_app.loyalty.col_description")}</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loyalty.loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">{t("user_app.loyalty.loading")}</TableCell>
              </TableRow>
            ) : transactions.length ? (
              transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    {transaction.createdDate ? new Date(transaction.createdDate).toLocaleString() : na}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type}
                      size="small"
                      color={
                        transaction.type === "EARN"
                          ? "success"
                          : transaction.type === "ADJUST"
                            ? "info"
                            : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={transaction.status} size="small" />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {transaction.points > 0 ? "+" : ""}
                    {formatPoints(transaction.points)}
                  </TableCell>
                  <TableCell align="right">{formatPoints(transaction.balanceAfter)}</TableCell>
                  <TableCell>
                    {transaction.sourceType || "-"}
                    {transaction.orderId ? ` #${transaction.orderId}` : ""}
                  </TableCell>
                  <TableCell>{transaction.description || "-"}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">{t("user_app.loyalty.empty")}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={loyalty.total}
          page={loyalty.page}
          onPageChange={loyalty.handleChangePage}
          rowsPerPage={loyalty.rowsPerPage}
          onRowsPerPageChange={loyalty.handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
    </>
  );
};

export default UserAppLoyaltyTab;
